import { assertNever } from '@tableau/data-structures';
import { TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import {
  SiteUrlsContext
} from '../../../data-catalog-shared';
import { fieldsWrapperMaxRows } from '../FieldDetails/FieldPropertyComponents';
import { FieldsReferencedByFieldQueryGrid } from '../FieldDetails/FieldsReferencedByFieldQueryGrid';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { getRowHeight } from '../sharedDisplayGrid/LinksDisplayGrid';
import { CalculationDetails } from './CalculationDetails';
import { SheetField, SheetFieldRemoteField, SheetFieldWithCalculation, SheetFieldWithFieldsConnection } from './SheetField';
import { VizClientFieldCardDescription } from './VizClientFieldCardDescription';
import {
  FieldCard,
  FieldCardDetailsWrapper,
  FieldCardDetailText,
  FieldCardDetailTitle,
  FieldCardDetailTitleWrapper,
  FieldCardHeaderFieldType,
  FieldCardHeaderWrapper,
  FieldCardWrapper,
  FieldsReferencingFieldsWrapper,
} from './VizClientFieldCardStyles';

export interface VizClientFieldCardProps {
  readonly field: SheetField;
  readonly workbookVizportalUrlId?: string;
}

export class VizClientFieldCard extends React.Component<VizClientFieldCardProps> {

  private renderHeader() {
    const fieldIcon = iconForField(this.props.field);
    return (
      <FieldCardHeaderWrapper data-tb-test-id={'data-details-field-card-header-test-id'}>
        {fieldIcon ? (
          <FieldCardHeaderFieldType>
            {FieldNameMappings[fieldIcon]()}
          </FieldCardHeaderFieldType>
        ) : null}
      </FieldCardHeaderWrapper>
    );
  }

  // TODO: use renderFieldsReferencedByField for BinFields when no router issue has been fixed see TFSID: 1021036
  private renderFieldsReferencedByField(field: SheetFieldWithFieldsConnection, title: string) {
    const count = field.fieldsConnection?.totalCount ?? null
    return (
      <>
        <FieldCardDetailTitleWrapper>
          <FieldCardDetailTitle>{title}</FieldCardDetailTitle>
        </FieldCardDetailTitleWrapper>
        <FieldsReferencingFieldsWrapper style={{ height: getRowHeight(count, fieldsWrapperMaxRows) }}>
          <FieldsReferencedByFieldQueryGrid
            fieldId={field.id}
            rowCount={count}
            targetBlank={true}
            useAnchor={true}
          />
        </FieldsReferencingFieldsWrapper>
      </>
    );
  }

  private renderCalculation(field: SheetFieldWithCalculation) {
    if (field && 'formula' in field) {
      return <CalculationDetails field={field} workbookVizportalUrlId={this.props.workbookVizportalUrlId} />;
    }

    return null;
  }

  private renderBinSize(binSize: string | null) {
    if (binSize === null) {
      return null;
    }

    return (
      <div data-tb-test-id={'data-details-bin-size-test-id'}>
        <FieldCardDetailTitleWrapper>
          <FieldCardDetailTitle>{Messages.FieldDetails_BinSize()}</FieldCardDetailTitle>
        </FieldCardDetailTitleWrapper>
        <FieldCardDetailText>{binSize}</FieldCardDetailText>
      </div>
    );
  }

  private renderFieldDetailsLink() {
    if (!this.props.workbookVizportalUrlId) {
      return null;
    }
    return (
      <SiteUrlsContext.Consumer>
        {urls =>
          <TextLinkWidget
            href={urls.workbookLineageFieldUrl(this.props.workbookVizportalUrlId ? this.props.workbookVizportalUrlId : '',
              this.props.field.id)}
            target='_blank'
            testId='data-details-field-view-more-link-test-id'
          >
            {Messages.ViewMore()}
          </TextLinkWidget>
        }
      </SiteUrlsContext.Consumer>
    );
  }

  private renderTypeSpecificFieldDetails(): React.ReactNode {
    if (this.props.field.__typename !== 'DatasourceField') {
      return this.renderTypeSpecificNonDatasourceFieldDetails(this.props.field);
    } else if (this.props.field.remoteField) {
      return this.renderTypeSpecificNonDatasourceFieldDetails(this.props.field.remoteField);
    } else {
      return null;
    }
  }

  private renderTypeSpecificNonDatasourceFieldDetails(field: SheetField | SheetFieldRemoteField): React.ReactNode {
    if (!field) {
      return null;
    }

    switch (field.__typename) {
      case 'BinField':
        return (
          <>
            {this.renderBinSize(field.binSize)}
            {this.renderFieldsReferencedByField(field, Messages.ReferencedFields())}
          </>
        );
      case 'CalculatedField':
        return (
          <>
            {this.renderCalculation(field)}
          </>
        );
      case 'GroupField':
        return (
          <>
            {this.renderFieldsReferencedByField(field, Messages.GroupedFields())}
          </>
        );
      case 'CombinedField':
      case 'CombinedSetField':
        return (
          <>
            {this.renderFieldsReferencedByField(field, Messages.CombinedFields())}
          </>
        );
      case 'HierarchyField':
      case 'SetField':
        return (
          <>
            {this.renderFieldsReferencedByField(field, Messages.ReferencedFields())}
          </>
        );
      case 'ColumnField':
      case 'DatasourceField':
        return null;
      default:
        assertNever(field);
        return null;
    }
  }

  public override render(): JSX.Element {
    return (
      <FieldCard data-tb-test-id={'data-details-field-card-test-id'}>
        <FieldCardWrapper>
          {this.renderHeader()}
          <FieldCardDetailsWrapper>
            <VizClientFieldCardDescription field={this.props.field} />
            {this.renderTypeSpecificFieldDetails()}
            {this.renderFieldDetailsLink()}
          </FieldCardDetailsWrapper>
        </FieldCardWrapper>
      </FieldCard>
    );
  }

}

